import { KeyboardEvent, MouseEvent, ReactNode, Ref } from "react";
import { Box, Checkbox, Tooltip, Typography } from "@mui/material";
import { AskText } from "./ChecklistRow";
import { EvidenceAttentionItem } from "../../types/evidence";

export interface RowStatus {
  text: string;
  /** How much an open ask matters; it colours the line. */
  ask: EvidenceAttentionItem["severity"] | null;
}

// One section of the document: the tick says whether it prints, the line says where its content
// comes from or what is still open, and whatever the agent can add sits underneath.
// Whatever the agent operates inside a row: a click on one never jumps the preview.
const CONTROLS = 'button, input, textarea, select, a, label, [role="button"], [role="combobox"], [role="option"], [role="listbox"], [role="menuitem"]';

export default function SectionRow({ heading, locked, notPrinted, checked, disabled, onToggle, chip, status, tag, onClick, onHover, selected, hovered, ref, children }: {
  heading: string;
  /** Always prints: the tick is shown but cannot be changed. */
  locked: boolean;
  /** A section that cannot be left out but has nothing to print for this order: an empty tick that cannot be changed. */
  notPrinted?: boolean;
  checked: boolean;
  disabled: boolean;
  onToggle: (checked: boolean) => void;
  chip?: ReactNode;
  status: RowStatus | null;
  tag?: { label: string; swatch: string };
  /** Set when the section rendered: a click on the row, outside its controls, scrolls the preview to it. */
  onClick?: () => void;
  /** Set alongside onClick: the pointer entering (true) or leaving (false) the row. */
  onHover?: (hovering: boolean) => void;
  /** Briefly set when the agent clicked this row's section in the preview. */
  selected?: boolean;
  /** Set while the pointer is on this row's section in the preview. */
  hovered?: boolean;
  ref?: Ref<HTMLDivElement>;
  children?: ReactNode;
}) {
  const onRowClick = onClick && ((event: MouseEvent) => {
    if (!(event.currentTarget as HTMLElement).contains(event.target as Node)) return;
    if ((event.target as HTMLElement).closest(CONTROLS)) return;
    onClick();
  });
  // Only a key press on the row itself: one inside a nested control is that control's.
  const onRowKeyDown = onClick && ((event: KeyboardEvent) => {
    if (event.target !== event.currentTarget || (event.key !== "Enter" && event.key !== " ")) return;
    if (event.key === " ") event.preventDefault();
    onClick();
  });

  return (
    <Box
      ref={ref}
      role="group"
      aria-label={heading}
      onClick={onRowClick}
      onKeyDown={onRowKeyDown}
      onMouseEnter={onHover && (() => onHover(true))}
      onMouseLeave={onHover && (() => onHover(false))}
      tabIndex={onClick ? 0 : undefined}
      sx={{ display: "flex", gap: 1, alignItems: "flex-start", py: 1, borderTop: 1, borderColor: "divider", "&:first-of-type": { borderTop: 0 }, bgcolor: selected ? "action.selected" : hovered ? "action.hover" : undefined }}
    >
      <Checkbox
        size="small"
        sx={{ p: 0, mt: 0.25 }}
        checked={!notPrinted && (locked || checked)}
        disabled={notPrinted || locked || disabled}
        onChange={(event) => onToggle(event.target.checked)}
        slotProps={{ input: { "aria-label": notPrinted ? `${heading} not printed` : locked ? `${heading} always prints` : `Include ${heading}` } }}
      />
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          component="div"
          sx={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 1, color: !notPrinted && (locked || checked) ? "text.primary" : "text.secondary" }}
        >
          {heading}
          {tag && (
            <Tooltip title={tag.label}>
              <Box component="span" aria-label={tag.label} sx={{ flexShrink: 0, width: 10, height: 10, borderRadius: "2px", bgcolor: tag.swatch }} />
            </Tooltip>
          )}
          {chip}
        </Typography>
        {status && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
            <AskText severity={status.ask}>{status.text}</AskText>
          </Typography>
        )}
        {children}
      </Box>
    </Box>
  );
}
